import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { compliance, ia } from "@/lib/api";
import { Shell } from "@/components/Shell";
import {
  ArrowLeft, Sparkles, Loader2, Upload, Users, FileText, Gavel, Clock,
  Plus, ShieldCheck, AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  ABERTO: "Aberto", EM_TRIAGEM: "Em Triagem", EM_INVESTIGACAO: "Em Investigação",
  MANIFESTACAO: "Manifestação", EM_ANALISE: "Em Análise", PARECER_EMITIDO: "Parecer Emitido",
  DECISAO_TOMADA: "Decisão", CONCLUIDO: "Concluído", ARQUIVADO: "Arquivado"
};
const STATUS_FLOW = ["ABERTO", "EM_TRIAGEM", "EM_INVESTIGACAO", "MANIFESTACAO", "EM_ANALISE", "PARECER_EMITIDO", "DECISAO_TOMADA", "CONCLUIDO"];

export default function ProcessoDetalhe() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("dados");
  const [loading, setLoading] = useState(false);

  const load = () => compliance.processo(id).then(r => setP(r.data));
  useEffect(() => { load(); }, [id]);

  const avancarStatus = async () => {
    const idx = STATUS_FLOW.indexOf(p.status);
    if (idx < 0 || idx >= STATUS_FLOW.length - 1) return;
    setLoading(true);
    try {
      await compliance.atualizarProcesso(id, { status: STATUS_FLOW[idx + 1] });
      toast.success(`Status → ${STATUS_LABEL[STATUS_FLOW[idx + 1]]}`);
      load();
    } finally { setLoading(false); }
  };

  if (!p) return <Shell><div className="p-10 text-center text-[#A1A4C7]"><Loader2 className="animate-spin mx-auto" /></div></Shell>;

  const currentIdx = STATUS_FLOW.indexOf(p.status);

  return (
    <Shell>
      <div className="p-6 md:p-10 fade-up max-w-6xl mx-auto">
        {/* Header */}
        <Link to="/compliance/ocorrencias" className="inline-flex items-center gap-2 text-[#B28F4B] hover:text-[#D4AF37] text-sm font-semibold mb-5">
          <ArrowLeft size={15} /> Voltar aos processos
        </Link>

        <div className="soeasy-card p-6 md:p-8 mb-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#B28F4B]/5 blur-[80px] rounded-full pointer-events-none" />
          <div className="relative">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-[#B28F4B] text-sm font-bold tracking-wider">{p.protocolo}</span>
                  <span className={`status-pill status-${p.status}`}>{STATUS_LABEL[p.status]}</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#6B6E8F]">{p.tipo_processo}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-medium text-white mb-2 leading-tight">{p.titulo}</h1>
                <p className="text-[#A1A4C7] text-sm leading-relaxed">{p.descricao}</p>
              </div>
              {currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1 && (
                <button
                  onClick={avancarStatus} disabled={loading}
                  data-testid="btn-avancar-status"
                  className="shrink-0 inline-flex items-center gap-2 bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60 text-sm"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                  Avançar → {STATUS_LABEL[STATUS_FLOW[currentIdx + 1]]}
                </button>
              )}
            </div>

            {/* meta chips */}
            <div className="flex flex-wrap gap-4 text-xs pt-4 border-t border-[#B28F4B]/10">
              <MetaChip label="Gravidade" value={p.gravidade} />
              <MetaChip label="Origem" value={p.origem} />
              <MetaChip label="Setor" value={p.setor_envolvido || "—"} />
              <MetaChip label="Investigado" value={p.colaborador_investigado || (p.anonimo ? "Anônimo" : "—")} />
              <MetaChip label="Aberto em" value={new Date(p.data_abertura).toLocaleDateString("pt-BR")} />
              <MetaChip label="Prazo" value={p.data_prazo ? new Date(p.data_prazo).toLocaleDateString("pt-BR") : "—"} />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 bg-[#131532] p-1.5 rounded-xl border border-[#B28F4B]/15">
          {[
            { id: "dados", label: "Dados", icon: FileText },
            { id: "timeline", label: "Timeline", icon: Clock },
            { id: "evidencias", label: `Evidências (${p.evidencias?.length || 0})`, icon: Upload },
            { id: "entrevistas", label: `Entrevistas (${p.entrevistas?.length || 0})`, icon: Users },
            { id: "parecer", label: `Pareceres (${p.pareceres?.length || 0})`, icon: Sparkles },
            { id: "decisao", label: "Decisão", icon: Gavel },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex-1 min-w-[100px] flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition ${
                tab === t.id ? "bg-[#B28F4B] text-[#1A1D42]" : "text-[#A1A4C7] hover:bg-[#1F2350] hover:text-white"
              }`}
            >
              <t.icon size={13} />
              <span className="whitespace-nowrap">{t.label}</span>
            </button>
          ))}
        </div>

        {tab === "dados" && <TabDados p={p} />}
        {tab === "timeline" && <TabTimeline events={p.timeline || []} />}
        {tab === "evidencias" && <TabEvidencias p={p} reload={load} />}
        {tab === "entrevistas" && <TabEntrevistas p={p} reload={load} />}
        {tab === "parecer" && <TabPareceres p={p} reload={load} />}
        {tab === "decisao" && <TabDecisao p={p} reload={load} />}
      </div>
    </Shell>
  );
}

function MetaChip({ label, value }) {
  return (
    <div>
      <div className="text-[9px] font-bold tracking-widest uppercase text-[#6B6E8F]">{label}</div>
      <div className="text-white text-sm font-semibold mt-0.5 capitalize">{value}</div>
    </div>
  );
}

function TabDados({ p }) {
  return (
    <div className="soeasy-card p-6 space-y-4 text-sm">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] mb-1">Descrição completa</div>
        <p className="text-white leading-relaxed">{p.descricao}</p>
      </div>
      {p.ocorrencia && (
        <div className="pt-4 border-t border-[#B28F4B]/10">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] mb-2">Registro de ocorrência</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[#A1A4C7]">
            <div><span className="text-[#6B6E8F] text-xs">Local:</span> <span className="text-white">{p.ocorrencia.local || "—"}</span></div>
            <div><span className="text-[#6B6E8F] text-xs">Data do fato:</span> <span className="text-white">{p.ocorrencia.data_fato || "—"}</span></div>
            <div><span className="text-[#6B6E8F] text-xs">Relatante:</span> <span className="text-white">{p.ocorrencia.anonimo ? "Anônimo" : (p.ocorrencia.relatante_nome || "—")}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabTimeline({ events }) {
  if (!events.length) return <EmptyBox icon={Clock} title="Sem eventos ainda" desc="Ações no processo aparecerão aqui automaticamente." />;
  return (
    <div className="soeasy-card p-6 md:p-8">
      <div className="relative pl-12">
        <div className="timeline-line" />
        <div className="space-y-6">
          {events.map((e, i) => (
            <div key={e.id} className="relative">
              <div className={`timeline-node absolute -left-[38px] top-1 ${i < events.length - 1 ? "timeline-node-past" : ""}`} />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#B28F4B] mb-0.5">{e.tipo_evento?.replace(/_/g, " ")}</div>
                <div className="text-white font-medium">{e.descricao}</div>
                <div className="text-[#6B6E8F] text-xs font-mono mt-1">{new Date(e.created_at).toLocaleString("pt-BR")}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TabEvidencias({ p, reload }) {
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [saving, setSaving] = useState(false);

  const salvar = async () => {
    if (!titulo) { toast.error("Informe título"); return; }
    setSaving(true);
    try {
      await compliance.criarEvidencia({ processo_id: p.id, tipo: "documento", titulo, descricao, conteudo: descricao });
      toast.success("Evidência coletada");
      setTitulo(""); setDescricao(""); reload();
    } catch (e) { toast.error("Erro"); } finally { setSaving(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="soeasy-card p-6">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] mb-3">Nova evidência</div>
        <div className="space-y-3">
          <input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Título" className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none" data-testid="input-evidencia-titulo"/>
          <textarea value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Descrição / conteúdo" rows={4} className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none resize-none"/>
          <button onClick={salvar} disabled={saving} data-testid="btn-salvar-evidencia" className="w-full bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Coletar
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {(p.evidencias || []).length === 0 && <EmptyBox icon={Upload} title="Sem evidências" desc="Adicione a primeira ao lado." />}
        {(p.evidencias || []).map(e => (
          <div key={e.id} className="soeasy-card p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="text-white font-semibold text-sm">{e.titulo}</div>
              <span className="text-[9px] uppercase tracking-wider font-bold text-[#B28F4B] bg-[#B28F4B]/10 px-2 py-0.5 rounded-full">Lacrada</span>
            </div>
            {e.descricao && <div className="text-[#A1A4C7] text-xs mb-2 line-clamp-2">{e.descricao}</div>}
            <div className="text-[#6B6E8F] text-[10px] font-mono">
              Hash MD5: {e.hash_md5?.substring(0, 16)}...
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TabEntrevistas({ p, reload }) {
  const [nome, setNome] = useState("");
  const [data, setData] = useState("");
  const [saving, setSaving] = useState(false);

  const salvar = async () => {
    if (!nome) { toast.error("Informe entrevistado"); return; }
    setSaving(true);
    try {
      await compliance.criarEntrevista({ processo_id: p.id, entrevistado_nome: nome, data_agendada: data, tipo: "entrevistado", modalidade: "presencial" });
      toast.success("Entrevista agendada");
      setNome(""); setData(""); reload();
    } catch { toast.error("Erro"); } finally { setSaving(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="soeasy-card p-6">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] mb-3">Agendar entrevista</div>
        <div className="space-y-3">
          <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome do entrevistado" className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none"/>
          <input type="datetime-local" value={data} onChange={e => setData(e.target.value)} className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none"/>
          <button onClick={salvar} disabled={saving} className="w-full bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Agendar
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {(p.entrevistas || []).length === 0 && <EmptyBox icon={Users} title="Sem entrevistas" desc="Agende ao lado." />}
        {(p.entrevistas || []).map(e => (
          <div key={e.id} className="soeasy-card p-4">
            <div className="text-white font-semibold text-sm mb-1">{e.entrevistado_nome}</div>
            <div className="text-[#A1A4C7] text-xs">Modalidade: {e.modalidade} · Status: {e.status}</div>
            {e.data_agendada && <div className="text-[#B28F4B] text-xs font-mono mt-1">{new Date(e.data_agendada).toLocaleString("pt-BR")}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function TabPareceres({ p, reload }) {
  const [conteudo, setConteudo] = useState("");
  const [conclusao, setConclusao] = useState("");
  const [recomendacao, setRecomendacao] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const gerarIA = async () => {
    setAiLoading(true);
    try {
      const { data } = await ia.parecer(p.id);
      setConteudo(data.parecer);
      toast.success("Minuta gerada pela IA. Revise antes de emitir.");
    } catch { toast.error("IA indisponível"); } finally { setAiLoading(false); }
  };

  const salvar = async () => {
    if (!conteudo || !conclusao) { toast.error("Preencha conteúdo e conclusão"); return; }
    setSaving(true);
    try {
      await compliance.criarParecer({ processo_id: p.id, conteudo, conclusao, recomendacao, gerado_por_ia: conteudo.length > 500 });
      toast.success("Parecer emitido");
      setConteudo(""); setConclusao(""); setRecomendacao(""); reload();
    } catch { toast.error("Erro"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      {(p.pareceres || []).map(pr => (
        <div key={pr.id} className="soeasy-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-[#B28F4B]"/>
            <div className="text-[10px] font-bold tracking-widest uppercase text-[#B28F4B]">Parecer emitido</div>
            {pr.gerado_por_ia && <span className="text-[9px] bg-[#B28F4B]/15 border border-[#B28F4B]/30 text-[#B28F4B] px-2 py-0.5 rounded-full uppercase font-bold tracking-wider">Assistido por IA</span>}
          </div>
          <div className="text-white text-sm whitespace-pre-wrap mb-3 leading-relaxed">{pr.conteudo}</div>
          <div className="pt-3 border-t border-[#B28F4B]/10 space-y-1 text-sm">
            <div><span className="text-[#6B6E8F] text-xs uppercase tracking-widest font-bold">Conclusão:</span> <span className="text-white">{pr.conclusao}</span></div>
            <div><span className="text-[#6B6E8F] text-xs uppercase tracking-widest font-bold">Recomendação:</span> <span className="text-white">{pr.recomendacao}</span></div>
          </div>
        </div>
      ))}

      <div className="soeasy-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B]">Emitir novo parecer</div>
          <button onClick={gerarIA} disabled={aiLoading} data-testid="btn-gerar-parecer-ia" className="text-xs px-3 py-1.5 bg-[#B28F4B]/15 border border-[#B28F4B]/40 text-[#B28F4B] hover:bg-[#B28F4B]/25 rounded-lg font-bold flex items-center gap-1.5 transition disabled:opacity-60">
            {aiLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>} Gerar com IA
          </button>
        </div>
        <div className="space-y-3">
          <textarea value={conteudo} onChange={e => setConteudo(e.target.value)} placeholder="Conteúdo do parecer técnico..." rows={8} className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none resize-none"/>
          <input value={conclusao} onChange={e => setConclusao(e.target.value)} placeholder="Conclusão" className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none"/>
          <input value={recomendacao} onChange={e => setRecomendacao(e.target.value)} placeholder="Recomendação" className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none"/>
          <button onClick={salvar} disabled={saving} data-testid="btn-emitir-parecer" className="w-full bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <ShieldCheck size={14}/>} Emitir Parecer
          </button>
        </div>
      </div>
    </div>
  );
}

function TabDecisao({ p, reload }) {
  const [tipo, setTipo] = useState("advertencia");
  const [fund, setFund] = useState("");
  const [medida, setMedida] = useState("");
  const [saving, setSaving] = useState(false);

  const salvar = async () => {
    if (!fund) { toast.error("Fundamentação obrigatória"); return; }
    setSaving(true);
    try {
      await compliance.criarDecisao({ processo_id: p.id, tipo_decisao: tipo, fundamentacao: fund, medida_aplicada: medida });
      toast.success("Decisão registrada");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Apenas Diretor/Admin"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      {(p.decisoes || []).map(d => (
        <div key={d.id} className="soeasy-card p-6" style={{ background: "linear-gradient(135deg, #1F2350 0%, #131532 100%)" }}>
          <div className="flex items-center gap-2 mb-3">
            <Gavel size={16} className="text-[#B28F4B]"/>
            <div className="text-[10px] font-bold tracking-widest uppercase text-[#B28F4B]">Decisão executada</div>
            <span className="status-pill status-DECISAO_TOMADA">{d.tipo_decisao}</span>
          </div>
          <div className="text-white text-sm leading-relaxed mb-3">{d.fundamentacao}</div>
          {d.medida_aplicada && <div className="text-[#A1A4C7] text-sm"><span className="text-[#6B6E8F] uppercase text-xs font-bold tracking-widest">Medida:</span> {d.medida_aplicada}</div>}
          <div className="text-[#6B6E8F] text-xs mt-3 pt-3 border-t border-[#B28F4B]/10 font-mono">
            {d.decidido_por_nome} · {new Date(d.data_decisao).toLocaleString("pt-BR")}
          </div>
        </div>
      ))}

      {p.decisoes?.length === 0 && (
        <div className="soeasy-card p-6">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] mb-3">Registrar decisão (apenas Diretor)</div>
          <div className="space-y-3">
            <select value={tipo} onChange={e => setTipo(e.target.value)} className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none">
              <option value="advertencia">Advertência</option>
              <option value="suspensao">Suspensão</option>
              <option value="demissao">Demissão</option>
              <option value="arquivamento">Arquivamento</option>
              <option value="encaminhamento_juridico">Encaminhamento Jurídico</option>
            </select>
            <textarea value={fund} onChange={e => setFund(e.target.value)} placeholder="Fundamentação da decisão..." rows={5} className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none resize-none"/>
            <input value={medida} onChange={e => setMedida(e.target.value)} placeholder="Medida aplicada (opcional)" className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none"/>
            <button onClick={salvar} disabled={saving} data-testid="btn-registrar-decisao" className="w-full bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Gavel size={14}/>} Registrar Decisão
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyBox({ icon: Icon, title, desc }) {
  return (
    <div className="soeasy-card p-8 text-center">
      <Icon size={30} className="mx-auto text-[#B28F4B] mb-2" strokeWidth={1.2}/>
      <div className="text-white font-heading">{title}</div>
      <div className="text-[#A1A4C7] text-xs mt-1">{desc}</div>
    </div>
  );
}
